import { CONTENT_TEAM_ROLES, roleAllowedFor } from '@kk/domain';
import { Card, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';

import { useContentRole, useSuperAdmin } from '@/auth/SessionProvider';
import { ColumnHead, LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { useAddTeamMember, useContentTeam, useErrorMessage, useRemoveTeamMember, useSetTeamRole } from '@/data/hooks';
import type { ContentTeamMember, ContentTeamRole } from '@/types/wire';

const c = deck.o17;

const initialsOf = (m: ContentTeamMember) =>
  (m.name ?? m.email ?? '')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

/**
 * The six roles as chips: the add form's choice, and an owner's control on each row. **Owner and Publisher are offered
 * only for a company address** — the API refuses them for anyone else, and a chip that can only fail is not drawn.
 */
function RoleChips({
  value,
  email,
  onPick,
  testID,
}: {
  value: ContentTeamRole | null;
  email: string;
  onPick: (r: ContentTeamRole) => void;
  testID: string;
}) {
  return (
    <div className="actions">
      {CONTENT_TEAM_ROLES.filter((r) => !email || roleAllowedFor(r, email)).map((r) => (
        <button
          key={r}
          type="button"
          className={`chip${value === r ? ' on' : ''}`}
          aria-pressed={value === r}
          title={c.roleHelp[r]}
          onClick={() => value !== r && onPick(r)}
          data-testid={`${testID}-${r}`}
        >
          {c.roles[r]}
        </button>
      ))}
    </div>
  );
}

/** The optional end date: a day, company time, from which access stops. Empty is none. */
function EndsOn({
  value,
  onChange,
  testID,
}: {
  value: string | undefined;
  onChange: (d: string | null) => void;
  testID: string;
}) {
  return (
    <label className="team-ends-on">
      <Text variant="bodySm" tone="muted">
        {c.endsOn}
      </Text>
      <input
        type="date"
        className="team-date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testID}
      />
    </label>
  );
}

/**
 * **O17 · Team** — the Quest Jr. content team at admin.kiddiekredit.com/team (Evan, 2026-09-26), on O08's staff pattern:
 * a list, add by email, remove, and the em dash where a row cannot be removed. Access is the list itself — read by the
 * API on every request — so adding someone needs no redeploy and removing someone takes effect on their next request.
 *
 * **The six roles** (2026-09-26): each row shows its role and what that role does, its scope, and an end date when it has
 * one. A company account that signed in with no role waits here as "requested" until an owner picks one.
 *
 * **Only an owner sees the controls.** A permanent owner (on the API stack) is never removable here, and neither is the
 * last owner who can act; both draw O08's em dash, never a disabled control.
 */
export function TeamScreen() {
  const onTeam = useSuperAdmin();
  const role = useContentRole();
  const errorMessage = useErrorMessage();
  const team = useContentTeam(onTeam);
  const add = useAddTeamMember();
  const setRole = useSetTeamRole();
  const remove = useRemoveTeamMember();
  const [email, setEmail] = useState('');
  const [newRole, setNewRole] = useState<ContentTeamRole>('writer');
  const [newEndsOn, setNewEndsOn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!onTeam) return <Status testID="admin-only">{deck.o06.adminOnly}</Status>;
  const owner = (team.data?.me.role ?? role) === 'owner';
  const members = team.data?.members ?? [];
  // owners who can act: a permanent one, or one who has signed in — the one the team is kept by
  const acting = members.filter((m) => m.role === 'owner' && (m.permanent || m.state === 'active')).length;
  // a company-only role picked before the address was typed out falls back rather than failing on Add
  const pickable = !email.trim() || roleAllowedFor(newRole, email.trim()) ? newRole : 'writer';

  const run = (fn: () => Promise<unknown>) => {
    setError(null);
    fn().catch((e: unknown) => setError(errorMessage(e)));
  };

  return (
    <div className="stack" data-testid="team">
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.subtitle}
          </Text>
        </div>
      </div>
      {team.error ? <Status>{errorMessage(team.error)}</Status> : null}

      {owner ? (
        <Card variant="quiet" radius="row" padding={18}>
          <div className="stack" data-testid="team-add">
            <TextField label={c.email} value={email} onChangeText={setEmail} testID="team-email" />
            <RoleChips value={pickable} email={email.trim()} onPick={setNewRole} testID="team-new-role" />
            <Text variant="bodySm" tone="muted" testID="team-new-role-help">
              {c.roleHelp[pickable]}
            </Text>
            <EndsOn value={newEndsOn ?? undefined} onChange={setNewEndsOn} testID="team-new-ends-on" />
            {email.trim() ? (
              <div className="actions">
                <WasabiButton
                  label={c.add}
                  size="sm"
                  fullWidth={false}
                  onPress={() =>
                    run(async () => {
                      await add.mutateAsync({
                        email: email.trim(),
                        role: pickable,
                        ...(newEndsOn ? { endsOn: newEndsOn } : {}),
                      });
                      setEmail('');
                      setNewEndsOn(null);
                    })
                  }
                  testID="team-add-submit"
                />
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}

      <Card variant="quiet" radius="row" padding={18}>
        <div className="stack">
          <div className="staff-head">
            <ColumnHead>{deck.o08.name}</ColumnHead>
            <ColumnHead>{deck.o08.role}</ColumnHead>
          </div>
          {members.map((m) => {
            const fixed = m.permanent || (m.role === 'owner' && m.state === 'active' && acting <= 1);
            const who = m.name ?? m.email ?? '';
            return (
              <div className="staff-row" key={m.id} data-testid={`member-${m.id}`}>
                <div className="staff-who">
                  <div className="initials" aria-hidden>
                    {initialsOf(m)}
                  </div>
                  <div className="stack-tight">
                    <Text variant="body" tone="ink">
                      {who}
                    </Text>
                    {m.state === 'invited' ? (
                      <Text variant="bodySm" tone="muted" testID={`invited-${m.id}`}>
                        {c.invited}
                      </Text>
                    ) : m.state === 'requested' ? (
                      <Text variant="bodySm" tone="muted" testID={`requested-${m.id}`}>
                        {c.requested}
                      </Text>
                    ) : m.email && m.email !== who ? (
                      <Text variant="bodySm" tone="muted">
                        {m.email}
                      </Text>
                    ) : null}
                    {m.endsOn && !owner ? (
                      <Text variant="bodySm" tone="muted" testID={`ends-on-${m.id}`}>
                        {c.endsOnShown(m.endsOn)}
                      </Text>
                    ) : null}
                  </div>
                </div>
                <div className="stack-tight">
                  {owner && !m.permanent ? (
                    <RoleChips
                      value={m.role}
                      email={m.email ?? ''}
                      onPick={(r) => run(() => setRole.mutateAsync({ id: m.id, role: r }))}
                      testID={`role-${m.id}`}
                    />
                  ) : (
                    <Text variant="bodySm" tone="muted" testID={`role-${m.id}`}>
                      {m.role ? c.roles[m.role] : c.requested}
                    </Text>
                  )}
                  <Text variant="bodySm" tone="muted" testID={`role-help-${m.id}`}>
                    {m.role ? `${c.roleHelp[m.role]} ${c.scope}.` : c.scope}
                  </Text>
                  {owner && !m.permanent && m.state !== 'requested' ? (
                    <EndsOn
                      value={m.endsOn}
                      onChange={(d) => run(() => setRole.mutateAsync({ id: m.id, endsOn: d }))}
                      testID={`ends-on-${m.id}`}
                    />
                  ) : null}
                </div>
                {!owner ? null : fixed ? (
                  <Text variant="bodySm" tone="muted" testID={`no-remove-${m.id}`}>
                    {deck.o08.noRemove}
                  </Text>
                ) : (
                  <LinkButton
                    label={deck.o08.remove}
                    onPress={() => {
                      if (window.confirm(c.confirmRemove(who))) run(() => remove.mutateAsync(m.id));
                    }}
                    testID={`remove-${m.id}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>
      {error ? <Status testID="team-error">{error}</Status> : null}
    </div>
  );
}
